import React, { useCallback, useEffect, useState } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, PlayCircle, CheckCircle2, Download, CalendarClock } from "lucide-react";
import { ReviewTable } from "../components/access-review/ReviewTable";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

function DueBanner({ nextDue, overdue }) {
  return (
    <div className={`flex items-center gap-3 p-4 rounded-sm border ${overdue ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`} data-testid="access-review-due-banner">
      <CalendarClock size={18} />
      <p className="text-sm">
        {nextDue ? (overdue ? `A review was due on ${fmtDate(nextDue)}.` : `Next review due ${fmtDate(nextDue)}.`) : "No access review has been completed yet."}
      </p>
    </div>
  );
}

function HistoryList({ reviews, activeId, onSelect }) {
  if (!reviews.length) return <p className="text-sm text-slate-500">No reviews yet.</p>;
  return (
    <div className="divide-y" data-testid="access-review-history">
      {reviews.map((r) => (
        <button key={r.id} onClick={() => onSelect(r.id)} className={`w-full text-left py-3 px-2 flex items-center justify-between hover:bg-slate-50 transition-colors ${activeId === r.id ? "bg-slate-50" : ""}`} data-testid={`review-history-${r.quarter}-${r.id.slice(0, 6)}`}>
          <div>
            <p className="text-sm font-medium">{r.quarter}</p>
            <p className="text-xs text-slate-500">Started {fmtDate(r.started_at)} by {r.started_by_name}</p>
          </div>
          <Badge className={`text-xs rounded-sm ${r.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {r.status === "completed" ? `Completed · ${r.removed ?? 0} removed` : "In progress"}
          </Badge>
        </button>
      ))}
    </div>
  );
}

export default function AccessReviewPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superuser" || (user?.roles || []).includes("admin");
  const [summary, setSummary] = useState(null);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadReview = useCallback(async (id) => {
    const r = await apiClient.get(`/access-reviews/${id}`);
    setReview(r.data);
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const r = await apiClient.get("/access-reviews");
      setSummary(r.data);
      const current = r.data.reviews.find((x) => x.status === "in_progress") || r.data.reviews[0];
      if (current) await loadReview(current.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load access reviews");
    }
  }, [loadReview]);

  useEffect(() => { if (isAdmin) loadSummary(); }, [loadSummary, isAdmin]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };

  const start = () => run(async () => { await apiClient.post("/access-reviews"); await loadSummary(); }, "Access review started");
  const complete = () => run(async () => { await apiClient.post(`/access-reviews/${review.id}/complete`); await loadSummary(); }, "Review completed and saved as evidence");
  const decide = (item, decision, note) => run(async () => {
    await apiClient.put(`/access-reviews/${review.id}/items/${item.user_id}`, { decision, note });
    await loadReview(review.id);
  }, decision === "remove" ? `${item.name || item.email} deactivated` : null);
  const restore = (item) => run(async () => { await apiClient.post(`/access-reviews/users/${item.user_id}/restore`); await loadReview(review.id); }, "Access restored");
  const resetMfa = (item) => run(async () => { await apiClient.post(`/security/users/${item.user_id}/reset-mfa`); await loadReview(review.id); }, "MFA reset — they'll set it up at next sign-in");

  if (!isAdmin) {
    return (
      <Card data-testid="access-review-denied">
        <CardContent className="py-16 text-center text-sm text-slate-500">Only organisation admins can run access reviews.</CardContent>
      </Card>
    );
  }
  if (!summary) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  const inProgress = review?.status === "in_progress";
  const pending = review ? review.items.filter((i) => i.decision === "pending").length : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="access-review-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Access Review</h1>
          <p className="text-slate-500 mt-1">Confirm every quarter that each person still needs access. Each review is saved as audit evidence.</p>
        </div>
        {!summary.reviews.some((r) => r.status === "in_progress") && (
          <Button onClick={start} disabled={busy} className="rounded-sm gap-2" data-testid="start-access-review-btn"><PlayCircle size={16} /> Start quarterly review</Button>
        )}
      </div>

      <DueBanner nextDue={summary.next_due} overdue={summary.overdue} />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">
        {review ? (
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle data-testid="access-review-title">{review.quarter} review</CardTitle>
                <CardDescription>
                  {inProgress ? `${review.total - pending} of ${review.total} decided` : `Completed ${fmtDate(review.completed_at)} by ${review.completed_by_name}`}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => window.open(`${apiClient.defaults.baseURL}/access-reviews/${review.id}/export`, "_blank")} data-testid="export-access-review-btn"><Download size={14} /> CSV</Button>
                {inProgress && (
                  <Button size="sm" className="rounded-sm gap-1.5" disabled={busy || pending > 0} onClick={complete} data-testid="complete-access-review-btn"><CheckCircle2 size={14} /> Complete review</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ReviewTable review={review} currentUserId={user?.id} onDecide={decide} onRestore={restore} onResetMfa={resetMfa} />
            </CardContent>
          </Card>
        ) : (
          <Card><CardContent className="py-16 text-center text-sm text-slate-500" data-testid="access-review-empty">Start your first quarterly review to check who has access.</CardContent></Card>
        )}
        <Card>
          <CardHeader><CardTitle className="text-base">History</CardTitle></CardHeader>
          <CardContent><HistoryList reviews={summary.reviews} activeId={review?.id} onSelect={(id) => run(() => loadReview(id))} /></CardContent>
        </Card>
      </div>
    </div>
  );
}
